import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Sprout, Handshake, PhoneCall } from "lucide-react";
import { createFarmerInterest } from "@/lib/api";
import { JALNA_TALUKAS, JALNA_VILLAGES, MAHARASHTRA_DISTRICTS } from "@/data/jalnaVillages";

const HERO = "https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&w=1600";

const INDIAN_STATES = [
  "Andhra Pradesh", "Bihar", "Chhattisgarh", "Gujarat", "Haryana", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan",
  "Tamil Nadu", "Telangana", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Other",
];

export default function FarmerInterest() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    full_name: "", phone: "", village: "", taluka: "",
    district: "Jalna", state: "Maharashtra",
    approximate_area_acres: "", number_of_trees: "", age_of_trees_years: "",
    expected_production_tons: "",
    interest_topic: "topic_sell",
    fpo_name: "", notes: "", consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const cleanPhone = form.phone.replace(/\D/g, "").slice(-10);
    if (cleanPhone.length !== 10) { toast.error(t("farmer_interest.invalid_phone")); return; }
    if (!form.consent) { toast.error(t("farmer_interest.consent_required")); return; }
    if (!form.full_name || !form.village) { toast.error(t("toast.fill_required")); return; }

    setBusy(true);
    try {
      await createFarmerInterest({
        full_name: form.full_name,
        phone: cleanPhone,
        village: form.village,
        taluka: form.taluka || null,
        district: form.district,
        state: form.state,
        approximate_area_acres: form.approximate_area_acres ? parseFloat(form.approximate_area_acres) : null,
        number_of_trees: form.number_of_trees ? parseInt(form.number_of_trees) : null,
        age_of_trees_years: form.age_of_trees_years ? parseInt(form.age_of_trees_years) : null,
        expected_production_tons: form.expected_production_tons ? parseFloat(form.expected_production_tons) : null,
        interest_topic: form.interest_topic,
        fpo_name: form.fpo_name || null,
        notes: form.notes || null,
        consent: true,
        preferred_language: i18n.language || "en",
      });
      setDone(true);
      toast.success(t("farmer_interest.success_title"));
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      if (code === "invalid_phone") toast.error(t("farmer_interest.invalid_phone"));
      else toast.error(t("toast.generic_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-farmer-interest">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 tr-hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-white">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">
            {t("farmer_interest.eyebrow")}
          </div>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">
            {t("farmer_interest.title_a")} <span className="text-[var(--tr-citrus-yellow)]">{t("farmer_interest.title_b")}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85">{t("farmer_interest.body")}</p>
        </div>
      </section>

      {/* Two step process */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <h2 className="tr-section-title text-2xl md:text-3xl">{t("farmer_interest.info_two_step_title")}</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="tr-card p-8" data-testid="fi-step1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--tr-deep-green)] text-white grid place-items-center font-bold">1</div>
              <PhoneCall className="w-5 h-5 text-[var(--tr-zest)]" />
            </div>
            <p className="mt-4 text-[var(--tr-deep-green)] leading-relaxed">{t("farmer_interest.info_step1")}</p>
          </div>
          <div className="tr-card p-8" data-testid="fi-step2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--tr-deep-green)] text-white grid place-items-center font-bold">2</div>
              <Sprout className="w-5 h-5 text-[var(--tr-zest)]" />
            </div>
            <p className="mt-4 text-[var(--tr-deep-green)] leading-relaxed">{t("farmer_interest.info_step2")}</p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        {done ? (
          <div data-testid="farmer-interest-success" className="tr-card p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-[var(--tr-lime)] mx-auto" />
            <h3 className="font-display font-extrabold text-3xl mt-4 text-[var(--tr-deep-green)]">
              {t("farmer_interest.success_title")}
            </h3>
            <p className="mt-4 text-[var(--tr-muted)] max-w-xl mx-auto">{t("farmer_interest.success_body")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/login/farmer" className="tr-pill-btn tr-btn-primary" data-testid="fi-post-login">
                <Handshake className="w-4 h-4" /> {t("farmer_interest.already_registered_cta")}
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="farmer-interest-form" className="tr-card p-8 lg:p-10 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={`${t("farmer_interest.f_full_name")}*`}>
                <input required data-testid="fi-full-name" className="tr-input" value={form.full_name} onChange={set("full_name")} />
              </Field>
              <Field label={`${t("farmer_interest.f_phone")}*`}>
                <input required inputMode="tel" data-testid="fi-phone" className="tr-input" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" />
              </Field>
              <Field label={t("farmer_interest.f_district")}>
                <select data-testid="fi-district" className="tr-input" value={form.district} onChange={(e) => setForm((f) => ({ ...f, district: e.target.value, taluka: "", village: "" }))}>
                  {MAHARASHTRA_DISTRICTS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </Field>
              <Field label={t("farmer_interest.f_taluka")}>
                {form.district === "Jalna" ? (
                  <select data-testid="fi-taluka" className="tr-input" value={form.taluka} onChange={(e) => setForm((f) => ({ ...f, taluka: e.target.value, village: "" }))}>
                    <option value="">— select —</option>
                    {JALNA_TALUKAS.map((tk) => <option key={tk}>{tk}</option>)}
                  </select>
                ) : (
                  <input data-testid="fi-taluka" className="tr-input" value={form.taluka} onChange={set("taluka")} />
                )}
              </Field>
              <Field label={`${t("farmer_interest.f_village")}*`}>
                {form.district === "Jalna" && form.taluka && JALNA_VILLAGES[form.taluka] ? (
                  <select required data-testid="fi-village" className="tr-input" value={form.village} onChange={set("village")}>
                    <option value="">— select village —</option>
                    {JALNA_VILLAGES[form.taluka].map((v) => <option key={v}>{v}</option>)}
                  </select>
                ) : (
                  <input required data-testid="fi-village" className="tr-input" value={form.village} onChange={set("village")} />
                )}
              </Field>
              <Field label={t("farmer_interest.f_state")}>
                <select data-testid="fi-state" className="tr-input" value={form.state} onChange={set("state")}>
                  {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={t("farmer_interest.f_area")}>
                <input type="number" step="0.1" inputMode="decimal" data-testid="fi-area" className="tr-input" value={form.approximate_area_acres} onChange={set("approximate_area_acres")} />
              </Field>
              <Field label="Number of Mosambi trees">
                <input type="number" min="0" inputMode="numeric" data-testid="fi-trees" className="tr-input" value={form.number_of_trees} onChange={set("number_of_trees")} />
              </Field>
              <Field label="Age of trees (years)">
                <input type="number" min="0" inputMode="numeric" data-testid="fi-tree-age" className="tr-input" value={form.age_of_trees_years} onChange={set("age_of_trees_years")} />
              </Field>
              <Field label="Expected production (tons / season)">
                <input type="number" step="0.1" inputMode="decimal" data-testid="fi-expected-prod" className="tr-input" value={form.expected_production_tons} onChange={set("expected_production_tons")} />
              </Field>
              <Field label={t("farmer_interest.f_topic")}>
                <select data-testid="fi-topic" className="tr-input" value={form.interest_topic} onChange={set("interest_topic")}>
                  <option value="topic_sell">{t("farmer_interest.topic_sell")}</option>
                  <option value="topic_join">{t("farmer_interest.topic_join")}</option>
                  <option value="topic_fpo">{t("farmer_interest.topic_fpo")}</option>
                  <option value="topic_other">{t("farmer_interest.topic_other")}</option>
                </select>
              </Field>
              <Field label={t("farmer_interest.f_fpo")} className="md:col-span-2">
                <input data-testid="fi-fpo" className="tr-input" value={form.fpo_name} onChange={set("fpo_name")} />
              </Field>
              <Field label={t("farmer_interest.f_notes")} className="md:col-span-2">
                <textarea data-testid="fi-notes" className="tr-input tr-textarea" value={form.notes} onChange={set("notes")} />
              </Field>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="fi-consent"
                checked={form.consent}
                onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)]"
              />
              <span className="text-sm text-[var(--tr-deep-green)] leading-relaxed">{t("farmer_interest.f_consent")}</span>
            </label>

            <div className="flex flex-wrap gap-4 items-center">
              <button type="submit" disabled={busy} data-testid="fi-submit" className="tr-pill-btn tr-btn-primary disabled:opacity-60">
                {busy ? t("common.submitting") : t("farmer_interest.f_submit")} <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/login/farmer" className="text-sm text-[var(--tr-muted)] underline hover:text-[var(--tr-deep-green)]" data-testid="fi-signin-link">
                {t("farmer_interest.already_registered")} {t("farmer_interest.already_registered_cta")}
              </Link>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="tr-label">{label}</span>
      {children}
    </label>
  );
}
